import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableSortLabel from '@mui/material/TableSortLabel';
import { createContext, FC, useState, useEffect } from 'react';
import { IconButton, Typography, Chip, Alert } from '@mui/material';
import AlertTitle from '@mui/material/AlertTitle';
import { useRouteMatch, useLocation, NavLink, useHistory } from "react-router-dom";
import { DocumentStatusDto } from './Chatbot.dto';
import { useChatbotContext } from '../../context';
import axios from 'axios';
import { useDocumentStatusAPI } from "../as2-onboarding-component/hooks/useClient";
import { useOktaAuth } from "@okta/okta-react";


interface ChatBotTableProps {
  list: any
}

const ChatBotTable: FC<ChatBotTableProps> = ({ list }) => {
  const { oktaAuth } = useOktaAuth();
  const documentStatusClient = useDocumentStatusAPI(oktaAuth.getAccessToken());
  let history = useHistory();
  //const historyContext = createContext();
  const [sortHeader, setSortHeader] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showErrorToast, setShowErrorToast] = useState(false);
  const { setSelectedHistoricalParams, closeDrawer } = useChatbotContext();

  const headers = list.length > 0 ? Object.keys(list[0]).filter(header => header !== 'ENVELOPE_NAME') : [];
  const hasIdField = headers.includes('ID');
  // console.log(headers);

  const getDisplayHeaderName = (header: string): string => {
    const headerMapping: { [key: string]: string } = {
      'TRANSACTION_TYPE': 'TRANSACTION TYPE',
      'SUPPLIER_ID': 'SUPPLIER ID',
      'ENVELOPE_NAME': 'ENVELOPE',
      'REFERENCE_VALUE': 'REFERENCE VALUE',
      'LAST_UPDATE_DATE': 'LAST UPDATE',
      'CREATE_DATE': 'CREATE DATE',
      'INT_SNDR_ID': 'INTERCHANGE SENDER',
      'GRP_RCVR_ID': 'GROUP RECEIVER',
      'GRP_SNDR_ID': 'GROUP SENDER',
      'INT_RCVR_ID': 'INTERCHANGE RECEIVER',
      'LICENSE_PLATE': 'LICENSE PLATE',
      'PACKLIST': 'PACKLIST',
      'PO': 'PURCHASE ORDER',
      'PART_NBR': 'PART NUMBER',
      'REF_NUM': 'REFERENCE NUMBER'
    };

    return headerMapping[header] || header;
  };

  const handleSort = (header: string): void => {
    if (sortHeader === header) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortHeader(header);
      setSortDirection('asc');
    }
  };

  const renderStatusChip = (status: string) => {
    if (status.toLowerCase() === 'error') {
      return (
        <Chip
          color={'error'}
          icon={<fuel-icon name={'error'}/>}
          label={'Error'}
          variant={'badge'}
        />
      );
    } else if (status.toLowerCase() === 'processing') {
      return (
        <Chip
          color={'informational'}
          icon={<fuel-icon name={'info'}/>}
          label={'Processing'}
          variant={'badge'}
        />
        );
    } else {
      return (
        <Chip
          color={'success'}
          icon={<fuel-icon name={'check_circle'}/>}
          label={'Success'}
          variant={'badge'}
        />
      );
    }
  };

  const closeToast = () => {
    setShowErrorToast(false);
  };

  // Timer para cerrar el toast automáticamente después de 5 segundos
  useEffect(() => {
    if (showErrorToast) {
      const timer = setTimeout(() => {
        setShowErrorToast(false);
      }, 5000); // 5 segundos

      return () => clearTimeout(timer); // Limpiar el timer si el componente se desmonta o showErrorToast cambia
    }
  }, [showErrorToast]);

  const handleDocumentHistoryProps = (row : any) =>{
    setSelectedHistoricalParams(row)
    closeDrawer();
    history.push(`/ediSelfServicePortal/documentHistory`);
  }

  const handleDownloadFiles = async (row : any) =>{
    try {
      let response: any;
      if(row.STATUS.toLowerCase()==="error"){
        response = await documentStatusClient.get(`/document-status/traceback-proxy?doc_id=${row.ID}&status_type=error`, {
          responseType: 'blob', // Important
        })
      } else{
          response = await documentStatusClient.get(`/document-status/traceback-proxy?doc_id=${row.ID}&status_type=success`, {
            responseType: 'blob', // Important
          })
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${row.ID}.txt`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link); // Optional, para limpiar el DOM
    } catch (error) {
      console.error('Error downloading file:', error);
      setShowErrorToast(true);
      return;
    }
  }

  const sortedList = [...list].sort((a, b) => {
    if (!sortHeader) return 0;
    const aValue = a[sortHeader];
    const bValue = b[sortHeader];
    if (aValue === bValue) return 0;
    return sortDirection === 'asc'
      ? aValue > bValue ? 1 : -1
      : aValue < bValue ? 1 : -1;
  });


  return (
    <>
    {showErrorToast && (
      <Alert
        onClose={closeToast}
        severity={'error'}
        variant={'toast'}
        sx={{ mb: 2 }}
      >
        <AlertTitle>{'Error'}</AlertTitle>
        {'Error downloading file.'}
      </Alert>
    )}
    {list.length>=50 ? <Typography sx={{color:'green', padding:3}}>You could just retrieve 30 as limit. It seems there could be more transactions. You can adjust the timeframe to get the expected transactions.</Typography> : <></>}
    <TableContainer sx={{
      overflowY: 'auto',
      overflowX:'auto',
      width:'100%',
      maxWidth:'100%',
      display:'block',
      maxHeight:400,
      border:'1px solid #ccc',
      borderRadius:2,
      boxSizing:'border-box'
    }} >
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow>
            {/* <TableCell sx={{ position:'sticky',top:0,backgroundColor:'#f5f5f5'}}>{'History Detail'}</TableCell> */}
            {hasIdField && <TableCell sx={{
              position:'sticky',
              top:0,
              backgroundColor:'#f5f5f5',
              zIndex:2,
              width: '80px',
              minWidth: '80px',
              padding: '8px'
            }}>{'EDI_FILE'}</TableCell>}
            {headers.map((header) => (
              header !== "ID" && (
                <TableCell key={header} sx={{
                  position:'sticky',
                  top:0,
                  backgroundColor:'#f5f5f5',
                  minWidth: '120px',
                  padding: '8px',
                  whiteSpace: 'nowrap'
                }}>
                  <TableSortLabel
                    active={sortHeader === header}
                    direction={sortDirection}
                    onClick={() => handleSort(header)}
                  >
                    {getDisplayHeaderName(header)}
                  </TableSortLabel>
                </TableCell>
              )
            ))}
          </TableRow>
        </TableHead>
        <TableBody>

          {sortedList.map((row, index) => (
            <TableRow key={index}>
          {/* <TableCell>
            <IconButton
            color={'onLight'}
            onClick={() =>handleDocumentHistoryProps(row)}
            >

              <fuel-icon name={'open_in_new'}/>
          </IconButton>
          </TableCell> */}
          {hasIdField && <TableCell sx={{ width: '80px', minWidth: '80px', padding: '8px' }}>
            <IconButton
            color={'onLight'}
            onClick={() =>handleDownloadFiles(row)}
            >

              <fuel-icon name={'download'}/>
          </IconButton>
          </TableCell>}
              {headers.map((header) => (
                header !== "ID" && (
                  <TableCell key={header} sx={{
                    minWidth: '120px',
                    padding: '8px',
                    whiteSpace: 'nowrap'
                  }}>
                    {header === "STATUS" ? renderStatusChip(row[header]) : row[header]}
                  </TableCell>
                )
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    </>

  );
};

export default ChatBotTable;
